// ajay:code all 27-08-26
import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Save, Lock, Plus, X } from "lucide-react";
import { productAPI, controlPlanAPI } from "../../services/api";
import StepBlock from "../../components/control-plan/StepBlock";
import ControlPlanSheetView from "../../components/control-plan/ControlPlanSheetView";
import ControlPlanSheetHeader from "../../components/control-plan/ControlPlanSheetHeader";

const emptyStep = () => ({
  step_no: "",
  step_name: "",
  machine_tools: "",
  control_method: "",
  reaction_plan: "",
  characteristics: [],
});

const STAGE_OPTIONS = ["Prototype", "Pre-Launch", "Production"];

// sheetView: only the client route passes this — renders the read-only
// tree as a real Excel-style grid on desktop. Fill/edit always uses cards.
export default function ControlPlanForm({
  basePath = "/company/control-plan",
  sheetView = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEditRoute = location.pathname.endsWith("/edit");
  const isAddMode = !id;
  const isLoadExisting = Boolean(id);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [status, setStatus] = useState("Draft");
  const [docMeta, setDocMeta] = useState(null); // { Company, Product }

  const [header, setHeader] = useState({
    document_no: "",
    page: "",
    issue_no: "",
    date: "",
    contact_person: "",
    contact_phone: "",
    stage: "",
    part_name: "",
    control_plan_no: "",
    date_origin: "",
    supplier_plant: "",
    supplier_code: "",
    performed_by: "",
    customer_name: "",
    customer_quality_approved_by: "",
    customer_quality_approved_date: "",
    customer_engg_approved_by: "",
    customer_engg_approved_date: "",
    part_number: "",
    release_change_level: "",
    release_date: "",
    model: "",
    rev_no: "",
    rev_date: "",
    rev_description: "",
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [steps, setSteps] = useState([]);

  const [loading, setLoading] = useState(isLoadExisting);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const isReadOnly = isLoadExisting && (!isEditRoute || status === "Final");

  useEffect(() => {
    if (isLoadExisting) return;
    productAPI.list().then((r) => {
      const active = (r.data || []).filter((p) => p.status === "Active");
      setProducts(active);
    });
  }, [isLoadExisting]);

  useEffect(() => {
    if (!isLoadExisting) return;
    setLoading(true);
    controlPlanAPI
      .getDocument(id)
      .then((r) => {
        const doc = r.data;
        setStatus(doc.status);
        setProductId(String(doc.product_id));
        setDocMeta({
          Company: doc.Company || null,
          Product: doc.Product || null,
        });
        setProducts([
          {
            id: doc.product_id,
            name: doc.Product?.name,
            code: doc.Product?.code,
          },
        ]);
        setHeader({
          document_no: doc.document_no || "",
          page: doc.page || "",
          issue_no: doc.issue_no || "",
          date: doc.date || "",
          contact_person: doc.contact_person || "",
          contact_phone: doc.contact_phone || "",
          stage: doc.stage || "",
          part_name: doc.part_name || "",
          control_plan_no: doc.control_plan_no || "",
          date_origin: doc.date_origin || "",
          supplier_plant: doc.supplier_plant || "",
          supplier_code: doc.supplier_code || "",
          performed_by: doc.performed_by || "",
          customer_name: doc.customer_name || "",
          customer_quality_approved_by: doc.customer_quality_approved_by || "",
          customer_quality_approved_date:
            doc.customer_quality_approved_date || "",
          customer_engg_approved_by: doc.customer_engg_approved_by || "",
          customer_engg_approved_date: doc.customer_engg_approved_date || "",
          part_number: doc.part_number || "",
          release_change_level: doc.release_change_level || "",
          release_date: doc.release_date || "",
          model: doc.model || "",
          rev_no: doc.rev_no || "",
          rev_date: doc.rev_date || "",
          rev_description: doc.rev_description || "",
        });
        setTeamMembers((doc.teamMembers || []).map((m) => m.member_name));
        setSteps(
          (doc.steps || []).map((s) => ({
            step_no: s.step_no || "",
            step_name: s.step_name,
            machine_tools: s.machine_tools || "",
            control_method: s.control_method || "",
            reaction_plan: s.reaction_plan || "",
            characteristics: (s.characteristics || []).map((c) => ({
              char_no: c.char_no || "",
              product_characteristic: c.product_characteristic || "",
              process_characteristic: c.process_characteristic || "",
              special_char_class: c.special_char_class || "",
              specification_tolerance: c.specification_tolerance || "",
              methods: (c.methods || []).map((m) => ({
                evaluation_technique: m.evaluation_technique || "",
                sample_size: m.sample_size || "",
                sample_frequency: m.sample_frequency || "",
                error_proofing: m.error_proofing || "",
                controlled_by_worker: Boolean(m.controlled_by_worker),
                controlled_by_inspector: Boolean(m.controlled_by_inspector),
              })),
            })),
          })),
        );
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [id, isLoadExisting]);

  const updateStep = (i, updated) =>
    setSteps((prev) => prev.map((s, idx) => (idx === i ? updated : s)));
  const addStep = () => setSteps((prev) => [...prev, emptyStep()]);
  const removeStep = (i) =>
    setSteps((prev) => prev.filter((_, idx) => idx !== i));

  const addTeamMember = () => setTeamMembers((prev) => [...prev, ""]);
  const updateTeamMember = (i, value) =>
    setTeamMembers((prev) => prev.map((m, idx) => (idx === i ? value : m)));
  const removeTeamMember = (i) =>
    setTeamMembers((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setErr("");
    if (!productId) return setErr("Select a part");
    if (steps.length === 0) return setErr("Add at least one process step");

    setSaving(true);
    try {
      const payload = {
        ...header,
        team_members: teamMembers.filter((m) => m.trim()),
        steps,
      };
      if (isEditRoute) {
        await controlPlanAPI.updateDocument(id, payload);
      } else {
        await controlPlanAPI.createDocument({
          product_id: productId,
          ...payload,
        });
      }
      navigate(basePath);
    } catch (e) {
      setErr(e.message || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    if (
      !window.confirm(
        "Finalize this Control Plan document? Once finalized it can NEVER be edited again, by anyone.",
      )
    )
      return;
    setSaving(true);
    try {
      await controlPlanAPI.finalizeDocument(id);
      setStatus("Final");
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedProduct = products.find(
    (p) => String(p.id) === String(productId),
  );

  if (loading)
    return <div style={{ padding: 28, color: "var(--text2)" }}>Loading...</div>;

  const inputStyle = {
    width: "100%",
    padding: "6px 10px",
    fontSize: 12,
    borderRadius: 6,
    border: "1px solid var(--border2)",
    background: "var(--surface2)",
    color: "var(--text)",
  };
  const labelStyle = {
    fontSize: 10,
    color: "var(--text3)",
    fontWeight: 700,
    marginBottom: 3,
    display: "block",
  };

  const textField = (field, label, type = "text") => (
    <div key={field}>
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        disabled={isReadOnly}
        style={inputStyle}
        value={header[field]}
        onChange={(e) => setHeader({ ...header, [field]: e.target.value })}
      />
    </div>
  );

  return (
    <div style={{ padding: 28, maxWidth: 1100, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <button onClick={() => navigate(basePath)} style={iconBtn}>
          <ArrowLeft size={16} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>
            {isAddMode
              ? "Add Control Plan"
              : isReadOnly
                ? "Control Plan (View)"
                : "Edit Control Plan"}
          </h1>
          <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            AIAG-style Control Plan
          </p>
        </div>
        {isLoadExisting && (
          <span
            className={`badge ${status === "Final" ? "badge-success" : "badge-danger"}`}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            {status === "Final" && <Lock size={11} />} {status}
          </span>
        )}
        {isLoadExisting && status === "Draft" && !isEditRoute && (
          <button
            className="btn-secondary"
            onClick={() => navigate(`${basePath}/${id}/edit`)}
          >
            Edit
          </button>
        )}
        {isLoadExisting && status === "Draft" && (
          <button
            className="btn-primary"
            onClick={finalize}
            disabled={saving}
            style={{
              background: "var(--danger)",
              borderColor: "var(--danger)",
            }}
          >
            Finalize (Lock)
          </button>
        )}
      </div>

      {err && (
        <div
          className="card"
          style={{ color: "var(--danger)", marginBottom: 16 }}
        >
          {err}
        </div>
      )}
      {isLoadExisting && status === "Final" && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            color: "var(--text2)",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Lock size={13} /> This document is Final and locked — no further
          edits are possible.
        </div>
      )}

      {isReadOnly ? (
        <ControlPlanSheetHeader
          doc={{ ...header, ...docMeta }}
          teamMembers={teamMembers}
        />
      ) : (
        <>
          {/* ── Header ── */}
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
              Header
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
                gap: 12,
              }}
            >
              <div>
                <label style={labelStyle}>Part No.</label>
                {!isAddMode ? (
                  <input
                    type="text"
                    disabled
                    style={inputStyle}
                    value={`${selectedProduct?.name || ""} (${selectedProduct?.code || ""})`}
                  />
                ) : (
                  <select
                    value={productId}
                    onChange={(e) => setProductId(e.target.value)}
                    style={inputStyle}
                  >
                    <option value="">— Select part —</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {textField("document_no", "Document No.")}
              {textField("page", "Page")}
              {textField("issue_no", "Issue No.")}
              {textField("date", "Date", "date")}
              <div>
                <label style={labelStyle}>Stage</label>
                <select
                  disabled={isReadOnly}
                  style={inputStyle}
                  value={header.stage}
                  onChange={(e) =>
                    setHeader({ ...header, stage: e.target.value })
                  }
                >
                  <option value="">— Select —</option>
                  {STAGE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              {textField("contact_person", "Contact Person")}
              {textField("contact_phone", "Contact Phone")}
              {textField("part_name", "Part Name")}
              {textField("model", "Model")}
              {textField("control_plan_no", "Control Plan No.")}
              {textField("date_origin", "Date (Origin)", "date")}
              {textField("supplier_plant", "Supplier's Production Plant")}
              {textField("supplier_code", "Supplier Code")}
              {textField("performed_by", "Performed By")}
              {textField("part_number", "Part Number")}
              {textField("release_change_level", "Release Change Level")}
              {textField("release_date", "Release Date", "date")}
              {textField("rev_no", "Rev No.")}
              {textField("rev_date", "Rev Date", "date")}
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>Modifications</label>
              <textarea
                rows={2}
                disabled={isReadOnly}
                style={inputStyle}
                value={header.rev_description}
                onChange={(e) =>
                  setHeader({ ...header, rev_description: e.target.value })
                }
              />
            </div>
          </div>

          {/* ── Customer ── */}
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
              Customer
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
                gap: 12,
              }}
            >
              {textField("customer_name", "Customer Name")}
              {textField(
                "customer_quality_approved_by",
                "Approved by Customer's Quality",
              )}
              {textField(
                "customer_quality_approved_date",
                "Quality Approval Date",
                "date",
              )}
              {textField(
                "customer_engg_approved_by",
                "Approved by Customer's Engg.",
              )}
              {textField(
                "customer_engg_approved_date",
                "Engg Approval Date",
                "date",
              )}
            </div>
            <p style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>
              Filled by the company for now — will move to customer sign-off
              later.
            </p>
          </div>

          {/* ── Team Members ── */}
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
              Team Members
            </h3>
            {teamMembers.map((m, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 8,
                  marginBottom: 6,
                  alignItems: "center",
                }}
              >
                <input
                  type="text"
                  value={m}
                  placeholder="Member name"
                  onChange={(e) => updateTeamMember(i, e.target.value)}
                  style={{
                    flex: 1,
                    padding: "5px 8px",
                    fontSize: 12,
                    borderRadius: 6,
                    border: "1px solid var(--border2)",
                    background: "var(--surface2)",
                    color: "var(--text)",
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeTeamMember(i)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--danger)",
                    cursor: "pointer",
                  }}
                >
                  <X size={13} />
                </button>
              </div>
            ))}
            <button type="button" onClick={addTeamMember} style={addBtnStyle}>
              <Plus size={12} /> Add Member
            </button>
          </div>
        </>
      )}

      {/* ── Process Steps ── */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
          Process Steps ({steps.length})
        </h3>
        {isReadOnly && sheetView && !isMobile ? (
          <ControlPlanSheetView steps={steps} />
        ) : (
          <>
            {steps.map((s, i) => (
              <StepBlock
                key={i}
                step={s}
                index={i}
                onChange={(updated) => updateStep(i, updated)}
                onDelete={() => removeStep(i)}
                readOnly={isReadOnly}
              />
            ))}
            {steps.length === 0 && (
              <div
                className="card"
                style={{
                  textAlign: "center",
                  color: "var(--text3)",
                  padding: 20,
                }}
              >
                {isReadOnly
                  ? "No process steps"
                  : "Add your first process step below"}
              </div>
            )}
          </>
        )}
        {!isReadOnly && (
          <button
            type="button"
            onClick={addStep}
            style={{ ...addBtnStyle, padding: "8px 16px", fontSize: 13 }}
          >
            <Plus size={14} /> Add Process Step
          </button>
        )}
      </div>

      {!isReadOnly && (
        <button
          className="btn-primary"
          onClick={submit}
          disabled={saving}
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <Save size={15} />{" "}
          {saving
            ? "Saving..."
            : isEditRoute
              ? "Save Changes"
              : "Submit Control Plan"}
        </button>
      )}
    </div>
  );
}

const iconBtn = {
  background: "var(--surface2)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: 6,
  cursor: "pointer",
  color: "var(--text2)",
  display: "flex",
};

const addBtnStyle = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  background: "var(--surface2)",
  border: "1px solid var(--border2)",
  borderRadius: 6,
  padding: "5px 10px",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--accent)",
  cursor: "pointer",
};
